"use client";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import { manualStockSchema } from "@/validations";
import {
  createManualStockMovement,
  fetchManualStockMovements,
} from "@/store/modules/stock/thunks";
import { InferType } from "yup";
import { useParams, useApiReady } from "@/hooks";
import { UseDataRangeFilter } from "@/types";
import { useEffect, useState, useMemo } from "react";
import { usePermission } from "../usePermission/usePermission";
import { useAuth } from "@/context/AuthContext";

const useStock = ({
  dateRange,
  onSuccess,
  type,
  closeModal,
}: UseDataRangeFilter & { type?: "Entry" | "Exit" }) => {
  const { params, handlePage, updateParams } = useParams();
  const { hasPermission } = usePermission();
  const { currency } = useAuth();
  const { isReady: isApiReady } = useApiReady();
  const [searchValue, setSearchValue] = useState<string>(params.search || "");

  // Estabilizar dateRange usando valores primitivos
  const startDateStr = dateRange?.start
    ? dateRange.start.toISOString().split("T")[0]
    : null;
  const endDateStr = dateRange?.end
    ? dateRange.end.toISOString().split("T")[0]
    : null;

  // Estabilizar params usando JSON.stringify
  const paramsStringified = useMemo(() => JSON.stringify(params), [params]);

  const dispatch: AppDispatch = useDispatch();

  type FormData = InferType<typeof manualStockSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(manualStockSchema),
    defaultValues: {},
  });

  const onSubmit = async (data: FormData) => {
    try {
      if (!hasPermission("create stock movements")) {
        return;
      }
      const result = await dispatch(createManualStockMovement(data));
      if (createManualStockMovement.fulfilled.match(result)) {
        reset();
        onSuccess?.();
        const payload = {
          ...params,
          start_date: dateRange.start.toISOString().split("T")[0],
          end_date: dateRange.end.toISOString().split("T")[0],
          filter: { ...(params.filter || {}), type },
        };
        dispatch(fetchManualStockMovements(payload));
        closeModal?.();
      }
    } catch (err) {
      console.error("Erro ao enviar movimentação manual:", err);
    }
  };

  useEffect(() => {
    if (!isApiReady) return;
    if (!hasPermission("read stock movements")) return;
    if (!startDateStr || !endDateStr) return;

    const payload = {
      ...params,
      start_date: startDateStr,
      end_date: endDateStr,
      filter: { ...(params.filter || {}), type },
    };

    dispatch(fetchManualStockMovements(payload));
  }, [isApiReady, paramsStringified, startDateStr, endDateStr, type, dispatch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const { movements, stats, loading, error } = useSelector(
    (state: any) => state.stock,
  );

  const handleSubmitFilter = () => {
    const payload: Record<string, any> = {
      ...params,
      start_date: dateRange.start.toISOString().split("T")[0],
      end_date: dateRange.end.toISOString().split("T")[0],
      filter: { ...(params.filter || {}), type },
    };

    if (hasPermission("read stock movements")) {
      dispatch(fetchManualStockMovements(payload));
    }
  };

  const totalItems = movements?.total ?? 0;
  const itemsPerPage = movements?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(movements?.current_page ?? 1);

  useEffect(() => {
    if (movements?.current_page && movements.current_page !== currentPage) {
      setCurrentPage(movements.current_page);
    }
  }, [movements?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  return {
    register,
    movements,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    loading,
    error,
    handleSubmitFilter,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    stats,
    currency,
    handleFilterChange,
    searchValue,
  };
};

export default useStock;
